import React from 'react';
import { Stack } from '@mui/material';
import { DashboardCard, DashboardCardSkeleton } from '../../../../../shared/components';
import { GaugeChart } from '../../../../../shared/components/charts';
import { AnnotatableChart } from '../../../../../shared/components/charts/AnnotatableChart';
import { roundToDecimal } from '../../shared/utils';
import type { MetricsFilters } from '../../../../../shared/api/types';
import type { HrvTrendPoint } from '../api/types';

interface HrvSectionProps {
  deptName: string;
  stats: { average_hrv: number | null; resting_heart_rate: number | null } | undefined;
  trends: HrvTrendPoint[] | undefined;
  isLoading: boolean;
  clientId: string | null;
  weatherFilters: MetricsFilters;
}

export const HrvSection: React.FC<HrvSectionProps> = ({
  deptName,
  stats,
  trends,
  isLoading,
  clientId,
  weatherFilters,
}) => {
  if (isLoading) {
    return (
      <Stack spacing={2}>
        <DashboardCardSkeleton height={200} />
        <DashboardCardSkeleton height={250} />
      </Stack>
    );
  }

  const hrvValue = stats?.average_hrv != null ? roundToDecimal(stats.average_hrv) : null;
  const hrvBaseline = { min: 45, max: 60 };

  const trendData =
    trends?.map((t) => ({
      label: t.period_start,
      recovery_score: t.recovery_score != null ? roundToDecimal(t.recovery_score) : null,
      strain: t.strain != null ? roundToDecimal(t.strain) : null,
    })) ?? [];

  return (
    <Stack spacing={2} sx={{ flex: 1 }}>
      <DashboardCard
        title='Average HRV'
        subtitle='Gauge shows how current HRV compares to baseline range.'
        expandable
        expandedContent={
          <GaugeChart
            value={hrvValue}
            min={0}
            max={120}
            baseline={hrvBaseline}
            unit='ms'
            title='Average HRV'
            useNeutralColors
            height={400}
          />
        }
      >
        <GaugeChart
          value={hrvValue}
          min={0}
          max={120}
          baseline={hrvBaseline}
          unit='ms'
          title='Average HRV'
          useNeutralColors
          height={200}
        />
      </DashboardCard>
      <DashboardCard
        title='Strain and Recovery Trend'
        subtitle={`${deptName} strain and recovery over time`}
        expandable
        expandedContent={
          <AnnotatableChart
            clientId={clientId}
            startDate={weatherFilters.start_date}
            endDate={weatherFilters.end_date}
            weatherFilters={weatherFilters}
            data={trendData}
            xAxisKey='label'
            series={[
              { dataKey: 'recovery_score', label: 'Recovery', color: '#4caf50', area: true },
              { dataKey: 'strain', label: 'Strain', color: '#f44336', area: true },
            ]}
            yAxisMin={0}
            yAxisMax={100}
            height={500}
          />
        }
      >
        <AnnotatableChart
          clientId={clientId}
          startDate={weatherFilters.start_date}
          endDate={weatherFilters.end_date}
          weatherFilters={weatherFilters}
          data={trendData}
          xAxisKey='label'
          series={[
            { dataKey: 'recovery_score', label: 'Recovery', color: '#4caf50', area: true },
            { dataKey: 'strain', label: 'Strain', color: '#f44336', area: true },
          ]}
          yAxisMin={0}
          yAxisMax={100}
          height={250}
        />
      </DashboardCard>
    </Stack>
  );
};
